import '@adornis/adornis-components/x-connection-status-alert.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-sidebar.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { authenticationGuard } from '@adornis/user-forms/authentication-guard.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { levumiAPI } from '@chancenwerk/campus-learning-content/api/levumiAPI.js';
import { ChancenComponentFabTaskBar } from '@chancenwerk/components/chancen-component-fab-task-bar.js';
import '@chancenwerk/components/chancen-component-login-redirect.js';
import { PageLoadEventLog } from '@chancenwerk/db/eventlogs/PageLoadEventlog.js';
import { UserAPI } from '@chancenwerk/master-data-management/users/api/UserAPI.js';
import { type ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { CWPollNotificationGuard } from '@chancenwerk/poll/client/cw-poll-notification.js';
import { watch } from '@lit-labs/preact-signals';
import { html, nothing, type PropertyValueMap } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { filter, take, takeUntil, tap } from 'rxjs';
import { campusRouter } from './campus-router.js';
import './components/campus-component-sidebar.js';
import { scrollToTop } from './globals.js';

@customElement('main-element')
export class MainElement extends ChemistryLitElement {
  size = new WindowSizeController(this);

  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());
  @state() private readonly _forceLevumi = new RXController(
    this,
    levumiAPI
      .subscribeForce()()
      .pipe(tap(() => this.requestUpdate())),
  );

  override connectedCallback() {
    super.connectedCallback();

    this._currentUser.observable.pipe(filter(Boolean), take(1), takeUntil(this.disconnected)).subscribe(async user => {
      try {
        await new PageLoadEventLog({ userID: user._id }).create();
      } catch {}
    });

    scrollToTop
      .asObservable()
      .pipe(takeUntil(this.disconnected))
      .subscribe(() => {
        this.shadowRoot?.querySelector('#scroll')?.scrollTo({ top: 0 });
      });
  }

  protected override firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.firstUpdated(_changedProperties);

    ChancenComponentFabTaskBar.addItem({
      _id: 'todo',
      rendereable: html` <cr-todo-button></cr-todo-button> `,
    });
  }

  override render() {
    return html`
      ${authenticationGuard(
        () => {
          return html` <chancen-component-fab-task-bar></chancen-component-fab-task-bar> `;
        },
        () => nothing,
      )}

      <cr-flex horizontal h100 ${css({ backgroundColor: this.colors.neutral.shade(50) })}>
        ${!this._levumiLock(false)
          ? authenticationGuard(
              () => html` <campus-component-sidebar></campus-component-sidebar> `,
              () => nothing,
            )
          : nothing}

        <cr-flex
          id="scroll"
          flex
          ${css({
            height: '100%',
            width: '100%',
            minWidth: '0',
            overflow: 'auto',
          })}
        >
          ${CWPollNotificationGuard({
            normal: () => this._levumiLock(true) ?? this._askVidisSchuljahr() ?? watch(campusRouter.outlet),
          })}
        </cr-flex>
      </cr-flex>

      <x-connection-status-alert .showDuration=${20000}></x-connection-status-alert>

      <cr-writer-toolbar></cr-writer-toolbar>
    `;
  }

  @state() private _newSchoolYear = 0;

  private _levumiLock(checkRoute: boolean) {
    // * Disable in production
    if (baseConfig.get('DEPLOYMENT') === 'production') {
      return;
    }

    if (checkRoute && campusRouter.currentRoute.value.route.name === 'campus-student-play') {
      return;
    }

    // * Ignore non-students
    if (!(this._currentUser.value instanceof Student)) {
      return;
    }

    // * Ignore vidis user
    const student = this._currentUser.value as Student;
    if (student.email.includes('vidisdummy')) {
      return;
    }

    if (this._forceLevumi.isLoading) {
      return;
    }

    if (!this._forceLevumi.value || this._forceLevumi.value.length === 0) {
      return;
    }

    return html`<campus-page-lessons-chancenkurve
      pageTitle="Deine CHANCENkurve"
      pageDescription="Um den CHANCENcampus vollständig nutzen zu können, musst du diese CHANCENkurve Tests machen."
      .showLessons=${this._forceLevumi.value}
    ></campus-page-lessons-chancenkurve>`;
  }

  private _askVidisSchuljahr() {
    if (!(this._currentUser.value instanceof Student)) {
      return;
    }

    const student = this._currentUser.value as Student;
    const schoolyear = student.schoolInformation?.jahrgangsstufe;

    if (schoolyear) {
      return null;
    }

    return html`<cr-flex crossaxis-center ${css({ height: '100%', justifyContent: 'center' })}>
      <cr-flex padding="lg" space="lg" ${css({ backgroundColor: 'white', borderRadius: this.sizes.borderRadius })}>
        <cr-h3>Willkommen im CHANCENCampus!</cr-h3>
        <cr-text> Bitte trag dein aktuelles Schuljahr ein, damit wir dir passende Inhalte anzeigen können. </cr-text>

        <cr-dropdown-selection
          placeholder="Jahrgangsstufe"
          .selectables=${[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]}
          @value-picked=${async (e: ValueEvent<number>) => {
            this._newSchoolYear = e.detail.value;
          }}
        ></cr-dropdown-selection>

        <cr-button
          @click=${async () => {
            await UserAPI.register.finalizeVidis(this._newSchoolYear)();
          }}
          >Speichern</cr-button
        >
      </cr-flex>
    </cr-flex>`;
  }
}
